import { useState, useEffect, useCallback, useRef } from 'react';
import { Form } from 'antd';
import { CustomerCategoryDTO, CustomerCategoryProductDTO, SubPartType } from "@/shared/api/model";

interface FormState {
    // Selected products
    selectedProducts: CustomerCategoryProductDTO[];
    // Grouped field configuration with UI changes - single source of truth
    groupedFieldConfiguration: any | null;
    // Flat fields array for backward compatibility (extracted from groupedFieldConfiguration)
    categoryFields: any[];
    // Selected user groups
    selectedGroups: any[];
    // Customer type and configurations
    customerType: 'INDIVIDUAL' | 'GROUP' | 'BUSINESS';
    groupConfig: any;
    businessConfig: any;
    // Category-wide UI configuration
    uiConfiguration: any;
    // Hierarchical relationship state
    isSubPart: boolean;
    parentCategoryId: number | undefined;
    subPartType: SubPartType | undefined;
    // Document and location requirements
    documentRequirements: any[];
    locationRequirements: any[];
}

interface UseCustomerCategoryFormStateProps {
    mode: 'create' | 'edit' | 'view';
    initialData: CustomerCategoryDTO | null;
    entityFields?: any;
}

export const useCustomerCategoryFormState = ({
    mode,
    initialData,
    entityFields
}: UseCustomerCategoryFormStateProps) => {
    const [form] = Form.useForm();
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Form state
    const [formState, setFormState] = useState<FormState>({
        selectedProducts: [],
        groupedFieldConfiguration: null,
        categoryFields: [],
        selectedGroups: [],
        customerType: 'INDIVIDUAL',
        groupConfig: null,
        businessConfig: null,
        uiConfiguration: {
            renderingMode: 'tabs',
            wizardOrientation: 'horizontal',
            showProgress: false,
            showGroupNavigation: true,
            validateAllGroups: true,
            allowGroupCollapse: false,
            showFieldCount: true,
            autoSave: false,
            theme: 'default',
            validationMode: 'onChange'
        },
        isSubPart: false,
        parentCategoryId: undefined,
        subPartType: undefined,
        documentRequirements: [],
        locationRequirements: []
    });

    // Initialize form with initial data - use ref to track if already initialized
    const initializedRef = useRef(false);
    const initialDataIdRef = useRef<number | undefined>(undefined);

    useEffect(() => {
        // Only initialize once, or when the category ID changes
        const categoryId = initialData?.id;
        const hasIdChanged = initialDataIdRef.current !== categoryId;
        
        if (initialData && mode !== 'create' && (!initializedRef.current || hasIdChanged)) {
            initializedRef.current = true;
            initialDataIdRef.current = categoryId;
            
            form.setFieldsValue({
                name: initialData.name,
                description: initialData.description,
                status: initialData.status,
                customerType: initialData.customerType || 'INDIVIDUAL'
            });

            // Extract flat fields from grouped configuration for backward compatibility
            const flatFields = initialData.groupedFieldConfiguration?.fieldGroups?.flatMap(
                (group: any) => group.fields || []
            ) || initialData.fields || [];

            setFormState({
                customerType: initialData.customerType || 'INDIVIDUAL',
                selectedProducts: initialData.products || [],
                selectedGroups: initialData.userGroups || [],
                groupConfig: initialData.groupConfig,
                businessConfig: initialData.businessConfig,
                uiConfiguration: initialData.uiConfiguration || {
                    renderingMode: 'tabs',
                    wizardOrientation: 'horizontal',
                    showProgress: false,
                    showGroupNavigation: true,
                    validateAllGroups: true,
                    allowGroupCollapse: false,
                    showFieldCount: true,
                    autoSave: false,
                    theme: 'default',
                    validationMode: 'onChange'
                },
                isSubPart: initialData.isSubPart || false,
                parentCategoryId: initialData.parentCategoryId,
                subPartType: initialData.subPartType,
                documentRequirements: initialData.documents || [],
                locationRequirements: initialData.locations || [],
                groupedFieldConfiguration: initialData.groupedFieldConfiguration || null,
                categoryFields: flatFields
            });
        } else if (mode === 'create' && !initializedRef.current) {
            initializedRef.current = true;
            
            // Initialize with defaults for create mode
            form.setFieldsValue({
                status: 'ACTIVE',
                customerType: 'INDIVIDUAL'
            });
            setFormState({
                customerType: 'INDIVIDUAL',
                selectedProducts: [],
                selectedGroups: [],
                groupedFieldConfiguration: null,
                categoryFields: [],
                groupConfig: null,
                businessConfig: null,
                uiConfiguration: {
                    renderingMode: 'tabs',
                    wizardOrientation: 'horizontal',
                    showProgress: false,
                    showGroupNavigation: true,
                    validateAllGroups: true,
                    allowGroupCollapse: false,
                    showFieldCount: true,
                    autoSave: false,
                    theme: 'default',
                    validationMode: 'onChange'
                },
                isSubPart: false,
                parentCategoryId: undefined,
                subPartType: undefined,
                documentRequirements: [],
                locationRequirements: []
            });
        }
    }, [initialData?.id, mode, form]); // Only depend on ID, not entire initialData object

    // Method to update grouped field configuration with UI changes
    const updateGroupedFieldConfiguration = useCallback((updatedConfig: any) => {
        console.log('=== UPDATE GROUPED FIELD CONFIGURATION DEBUG ===');
        console.log('Updated Config:', updatedConfig);
        
        setFormState(prev => ({
            ...prev,
            groupedFieldConfiguration: updatedConfig
        }));
        
        // No need to flatten fields - grouped configuration is the single source of truth
        
        console.log('=== END UPDATE GROUPED FIELD CONFIGURATION DEBUG ===');
    }, []);

    // Method to update grouped field configuration - simplified since we only use grouped structure
    const updateFieldsFromGroupedConfiguration = useCallback((groupedConfig: any) => {
        console.log('=== UPDATE GROUPED FIELD CONFIGURATION DEBUG ===');
        console.log('Grouped Config:', groupedConfig);
        
        // Extract flat fields array from grouped configuration for backward compatibility
        const flatFields = groupedConfig?.fieldGroups?.flatMap((group: any) => group.fields || []) || [];
        console.log('Extracted Flat Fields Count:', flatFields.length);
        
        setFormState(prev => ({
            ...prev,
            groupedFieldConfiguration: groupedConfig,
            categoryFields: flatFields // Populate categoryFields for submission
        }));
        
        console.log('=== END UPDATE GROUPED FIELD CONFIGURATION DEBUG ===');
    }, []); // Remove mode dependency - not needed

    // Update form state functions
    const updateFormState = useCallback((updates: Partial<FormState>) => {
        setFormState(prev => ({ ...prev, ...updates }));
    }, []);

    const handleCustomerTypeChange = useCallback((value: 'INDIVIDUAL' | 'GROUP' | 'BUSINESS') => {
        setFormState(prev => {
            const updates: Partial<FormState> = { customerType: value };

            // Reset configurations when type changes
            if (value !== 'GROUP') {
                updates.groupConfig = null;
            } else if (!prev.groupConfig) {
                // Initialize with default group config
                updates.groupConfig = {
                    minMembers: 5,
                    requireRelationship: false,
                    requireSameLocation: false,
                    requireConstitution: false,
                    requireOfficials: false,
                    requireMeetingRecords: false
                };
            }

            if (value !== 'BUSINESS') {
                updates.businessConfig = null;
            } else if (!prev.businessConfig) {
                // Initialize with default business config
                updates.businessConfig = {
                    requireSignatories: false,
                    requireShareholders: false,
                    requireDirectors: false,
                    requireRegistrationCert: false,
                    requireTaxId: false,
                    requireBusinessLicense: false,
                    requireFinancialStatements: false,
                    requireBoardResolutions: false
                };
            }

            return { ...prev, ...updates };
        });
    }, []);

    // Product management functions
    const handleAddProduct = useCallback((glNo: string, allProducts: any[]) => {
        const product = allProducts?.find(p => p.glNo === glNo);
        if (!product) {
            console.error('Product not found for glNo:', glNo);
            return;
        }

        setFormState(prev => {
            const exists = prev.selectedProducts.some(p => p.glNo === product.glNo);
            if (exists) return prev;

            const newProduct: CustomerCategoryProductDTO = {
                glNo: product.glNo,
                productName: product.productName,
                isRequired: false,
                isDefault: false,
                productType: product.productType,
                productTypeName: product.productTypeName,
                displayOrder: prev.selectedProducts.length
            };

            return {
                ...prev,
                selectedProducts: [...prev.selectedProducts, newProduct]
            };
        });
    }, []);

    const handleRemoveProduct = useCallback((glNo: string) => {
        setFormState(prev => ({
            ...prev,
            selectedProducts: prev.selectedProducts.filter(p => p.glNo !== glNo)
        }));
    }, []);

    const handleProductPropertyChange = useCallback((glNo: string, prop: string, value: boolean) => {
        setFormState(prev => ({
            ...prev,
            selectedProducts: prev.selectedProducts.map(p => {
                if (p.glNo === glNo) {
                    return { ...p, [prop]: value };
                }
                return p;
            })
        }));
    }, []);

    // Field management functions - updated to work with grouped field configuration
    const handleFieldPropertyChange = useCallback((fieldName: string, entityType: string, prop: string, value: any) => {
        console.log('=== FORM STATE FIELD PROPERTY CHANGE DEBUG ===');
        console.log('Field Name:', fieldName);
        console.log('Entity Type:', entityType);
        console.log('Property:', prop);
        console.log('New Value:', value);
        
        setFormState(prev => {
            if (!prev.groupedFieldConfiguration?.fieldGroups) {
                console.warn('No grouped field configuration available');
                return prev;
            }
            
            let fieldFound = false;
            const updatedGroupedConfig = {
                ...prev.groupedFieldConfiguration,
                fieldGroups: prev.groupedFieldConfiguration.fieldGroups.map((group: any) => {
                    if (!group.fields) return group;
                    
                    const updatedFields = group.fields.map((field: any) => {
                        if (field.fieldName === fieldName) {
                            fieldFound = true;
                            const updatedField = { ...field, [prop]: value };
                            console.log('Updated field in group:', group.groupName, updatedField);
                            return updatedField;
                        }
                        return field;
                    });
                    
                    return { ...group, fields: updatedFields };
                })
            };
            
            if (!fieldFound) {
                console.warn('Field not found in grouped configuration:', { fieldName, entityType });
                return prev; // Return unchanged if field not found
            }
            
            console.log('Updated grouped field configuration');
            console.log('=== END FORM STATE FIELD PROPERTY CHANGE DEBUG ===');
            
            return {
                ...prev,
                groupedFieldConfiguration: updatedGroupedConfig
            };
        });
    }, []);

    // User group management functions
    const handleAddUserGroup = useCallback((groupCode: string) => {
        setFormState(prev => {
            const exists = prev.selectedGroups.some(g => g.groupCode === groupCode);
            if (exists) return prev;

            const newGroup = {
                groupCode,
                canUseFreestyle: false,
                isDefault: prev.selectedGroups.length === 0
            };

            return {
                ...prev,
                selectedGroups: [...prev.selectedGroups, newGroup]
            };
        });
    }, []);

    const handleRemoveUserGroup = useCallback((groupCode: string) => {
        setFormState(prev => ({
            ...prev,
            selectedGroups: prev.selectedGroups.filter(g => g.groupCode !== groupCode)
        }));
    }, []);

    const handleGroupPropertyChange = useCallback((groupCode: string, prop: string, value: boolean) => {
        setFormState(prev => {
            if (prop === 'isDefault' && value === true) {
                return {
                    ...prev,
                    selectedGroups: prev.selectedGroups.map(g => ({
                        ...g,
                        isDefault: g.groupCode === groupCode
                    }))
                };
            } else {
                return {
                    ...prev,
                    selectedGroups: prev.selectedGroups.map(g => {
                        if (g.groupCode === groupCode) {
                            return { ...g, [prop]: value };
                        }
                        return g;
                    })
                };
            }
        });
    }, []);

    // Create grouped field submission from current form state
    const createGroupedFieldSubmission = useCallback(() => {
        console.log('=== CREATING GROUPED FIELD SUBMISSION ===');
        console.log('Current groupedFieldConfiguration:', formState.groupedFieldConfiguration);
        
        // Return the grouped field configuration directly - single source of truth
        return formState.groupedFieldConfiguration;
    }, [formState.groupedFieldConfiguration]);

    // Helper functions for group display names, orders, and icons
    const getGroupDisplayName = (groupKey: string): string => {
        const groupNames: Record<string, string> = {
            'basic_info': 'Basic Information',
            'contact_info': 'Contact Information',
            'personal_details': 'Personal Details',
            'financial_info': 'Financial Information',
            'employment_info': 'Employment Information',
            'business_info': 'Business Information',
            'guarantor_info': 'Guarantor Information',
            'collateral_info': 'Collateral Information',
            'default': 'General Information'
        };
        
        return groupNames[groupKey] || groupKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const getGroupDisplayOrder = (groupKey: string): number => {
        const groupOrders: Record<string, number> = {
            'basic_info': 1,
            'contact_info': 2,
            'personal_details': 3,
            'employment_info': 4,
            'business_info': 5,
            'financial_info': 6,
            'guarantor_info': 7,
            'collateral_info': 8,
            'default': 99
        };
        
        return groupOrders[groupKey] || 99;
    };

    const getGroupIcon = (groupKey: string): string => {
        const groupIcons: Record<string, string> = {
            'basic_info': 'user',
            'contact_info': 'phone',
            'personal_details': 'idcard',
            'employment_info': 'team',
            'business_info': 'shop',
            'financial_info': 'dollar',
            'guarantor_info': 'safety',
            'collateral_info': 'bank',
            'default': 'folder'
        };
        
        return groupIcons[groupKey] || 'folder';
    };

    return {
        form,
        formState,
        isSubmitting,
        setIsSubmitting,
        updateFormState,
        updateFieldsFromEntityFields: updateFieldsFromGroupedConfiguration, // Alias for backward compatibility
        updateFieldsFromGroupedConfiguration,
        createGroupedFieldSubmission,
        handleCustomerTypeChange,
        handleAddProduct,
        handleRemoveProduct,
        handleProductPropertyChange,
        handleFieldPropertyChange,
        handleAddUserGroup,
        handleRemoveUserGroup,
        handleGroupPropertyChange,
    };
};
